import { FileExcelOutlined, MailOutlined } from "@ant-design/icons";
import { orvalClient } from "@api/client";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { App, Button, Space, Tooltip } from "antd";
import { useState } from "react";

const parseFilename = (contentDisposition?: string): string | undefined => {
  if (!contentDisposition) {
    return undefined;
  }

  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const asciiMatch = /filename\s*=\s*"?([^"]+)"?/i.exec(contentDisposition);
  return asciiMatch?.[1];
};

interface AxiosLikeDownloadError {
  isAxiosError?: boolean;
  message?: string;
  response?: {
    data?: unknown;
  };
}

const isAxiosLikeDownloadError = (err: unknown): err is AxiosLikeDownloadError =>
  Boolean(
    err &&
      typeof err === "object" &&
      ((err as { isAxiosError?: unknown }).isAxiosError === true || "response" in err),
  );

const extractDownloadErrorMessage = async (err: unknown): Promise<string> => {
  if (isAxiosLikeDownloadError(err)) {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        try {
          const json = JSON.parse(text) as { message?: string; error?: string; detail?: string };
          return (
            json.message || json.error || json.detail || err.message || "Не удалось скачать файл"
          );
        } catch {
          return text || err.message || "Не удалось скачать файл";
        }
      } catch {
        return err.message || "Не удалось скачать файл";
      }
    }
  }
  return extractErrorMessage(err);
};

interface ExportButtonProps {
  url?: string;
  disabled?: boolean;
  onSend?: () => void;
  sendDisabled?: boolean;
}

const ExportButton = ({ url, disabled, onSend, sendDisabled }: ExportButtonProps) => {
  const { message } = App.useApp();
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    if (!url || downloading) {
      return;
    }

    try {
      setDownloading(true);
      const response = await orvalClient.get<Blob>(url, {
        responseType: "blob",
        timeout: 180_000,
      });

      const contentType =
        (response.headers?.["content-type"] as string | undefined) ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const filename =
        parseFilename(response.headers?.["content-disposition"] as string | undefined) ||
        "report.xlsx";

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: contentType });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      message.error(await extractDownloadErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const isDisabled = disabled || !url;
  const sendButton = onSend ? (
    <Tooltip title={sendDisabled ? "Недоступно" : "Отправить на email"}>
      <Button icon={<MailOutlined />} disabled={sendDisabled} onClick={onSend}>
        Отправить
      </Button>
    </Tooltip>
  ) : null;

  return (
    <Space>
      {sendButton}
      <Tooltip title={isDisabled ? "Заполните фильтры для экспорта" : "Скачать XLSX"}>
        <Button
          icon={<FileExcelOutlined />}
          disabled={isDisabled || downloading}
          loading={downloading}
          onClick={handleClick}
        >
          Экспорт в Excel
        </Button>
      </Tooltip>
    </Space>
  );
};

export default ExportButton;
